import { asArray } from "@/lib/asArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";   // Batch 22 · Add-on 1: the link row
import { useEngagement } from "@/contexts/EngagementContext";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown, ChevronRight, EyeOff, RotateCcw, ArrowUp, ArrowDown,
  FolderOpen, FileText, GripVertical, Download, History, Search, X,
  Library,
} from "lucide-react";
import FormRenderer from "@/components/forms/FormRenderer";
import { FormPermanentDeleteDialog } from "@/pages/DocumentLibrary";
import { ReviewControls, ReviewCountsProvider } from "@/components/reviewpoints/ReviewPointsPanel";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import AddFromLibraryDialog from "@/components/AddFromLibraryDialog";
import Ref from "@/components/Ref";
import { askPrompt } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { FAR_ROUTE, isFarForm, isLegacy316a, opensFarScreen } from "@/lib/farForms";   // Batch 33 · B33-4 / PR 51 · P51-B2

const TAB_LABELS = {
  planning: "Planning",
  risk_assessment_forms: "Risk Assessment Forms",
  completion: "Completion",
  specialized_and_other_considerations: "Specialized & Other Considerations",
  acceptance: "Acceptance & Continuance",
  audit_response: "Audit Response",
  controls: "Tests of Controls",
  fieldwork_a9003: "Opening Balances",
  confirmations_forms: "Confirmations Forms",
  head_folders: "Head Folders",
};

// Tab → review-point target_type. Tabs outside this map (specialized,
// ISQM) do NOT support review points per the module spec.
const TAB_RP_TARGET = {
  planning: "planning_form",
  risk_assessment_forms: "risk_assessment_form",
  completion: "completion_form",
};

// HTML5 drag-and-drop helper — stores drag payload on window to survive across renders.
const _DND = { source: null };

// Natural sort key for ref numbers like "100(a)", "102(b)", "104", "12".
// Returns [numericPart, suffix] so 100(a) < 100(b) < 101.
function refKey(ref) {
  if (!ref) return [99999, ""];
  const m = String(ref).trim().match(/^(\d+)(.*)$/);
  // Batch 20 · A4 (F-140): a revised version reads "101 (a) revised 2025" — the space before "(a)" must not sort it ahead of
  // "101(a)"; the suffix is compared without its leading space, so the revised version sits right after the current one.
  if (m) return [parseInt(m[1], 10), (m[2] || "").trim().toLowerCase()];
  return [99998, String(ref).toLowerCase()];
}

function cmpRef(a, b) {
  const [an, as] = refKey(a);
  const [bn, bs] = refKey(b);
  if (an !== bn) return an - bn;
  return as < bs ? -1 : as > bs ? 1 : 0;
}

/* STEP 10 · C2 (Founder Errors tab #5, live 11-Oct-2026 /planning "Cannot read properties of undefined (reading …)"): the progress
   reply is read as progress.per_tab / progress.per_form on every render — a reply with no body (undefined / null / not an object)
   used to replace the state and crash the page. Any reply that is not an object reads as "no progress yet". */
export function progressOf(data) {
  const d = data && typeof data === "object" && !Array.isArray(data) ? data : {};
  return { ...d, per_tab: d.per_tab && typeof d.per_tab === "object" ? d.per_tab : {},
           per_form: d.per_form && typeof d.per_form === "object" ? d.per_form : {} };
}

export default function FormsPage({ tabId, titleKey }) {
  const { lang, dict, t } = useI18n();
  const tabLabel = (id) => dict.formsPage?.tabLabels?.[id] || TAB_LABELS[id] || id;
  // Batch 22 · Add-on 1 (item 3): an optional page title — /controls/203a is "Control Design (203(a))", not the tab's
  // "Tests of Controls" (the a9002 page keeps that title); the PDF file name follows it too.
  const pageTitle = titleKey ? t(titleKey) : tabLabel(tabId);
  const navigate = useNavigate();
  const { currentEngId } = useEngagement();
  const [searchParams, setSearchParams] = useSearchParams();
  const [library, setLibrary] = useState(null);
  const [progress, setProgress] = useState({ per_tab: {}, per_form: {} });
  const [openFolders, setOpenFolders] = useState({});
  const [openFormId, setOpenFormId] = useState(null);
  const [highlight, setHighlight] = useState("");
  const [prefs, setPrefs] = useState({ folder_order: [], form_order: {} });
  const [rollFwdOpen, setRollFwdOpen] = useState(false);
  const [carryAddons, setCarryAddons] = useState(true);
  const [rollBusy, setRollBusy] = useState(false);
  // Round 3+ Document Library — set of formIds included in THIS engagement.
  // Server may return `included_form_ids` on progress; if absent (older
  // engagements pre-migration), we render every library form (implicit
  // defaults) — matches the backend `_engagement_included_ids` fallback.
  const includedIds = useMemo(() => {
    const arr = progress?.included_form_ids;
    return arr && Array.isArray(arr) ? new Set(arr) : null;
  }, [progress]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Batch 0 corrections E — every forms tab exposes the universal "Add from library" popup.
  const [libOpen, setLibOpen] = useState(false);
  // Batch 0 corrections F — engagement type profile drives review-body filtering.
  const [engType, setEngType] = useState("audit");
  // PR 51 · P51-B2: a 316(a) link waits for the type (on a review engagement it opens the form dialog read-only)
  const [engTypeReady, setEngTypeReady] = useState(false);
  // K5 — set of audit-only formIds for this engagement (empty on audits).
  // Fed by /profile.audit_only_forms and used to (a) let audit-only rows
  // stay visible on the review Planning list and (b) apply the same
  // grey + 🔒 chip the sidebar uses (A1 rule).
  const [auditOnlyForms, setAuditOnlyForms] = useState(new Set());

  // NEW: ref-no search state
  const [refQuery, setRefQuery] = useState("");
  const [refFocus, setRefFocus] = useState(false);
  const refInputRef = useRef(null);

  // NEW: sort mode (folder | ref-no), persisted per-tab in localStorage.
  const sortKey = `azex.forms.sort.${tabId}`;
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem(sortKey) || "folder"; } catch { return "folder"; }
  });
  useEffect(() => {
    try { localStorage.setItem(sortKey, sortMode); } catch {}
  }, [sortKey, sortMode]);

  // Deep-link support:
  //   ?openForm=<formId>&highlight=item-XXX   (existing – used by search links)
  //   ?ref=104(a)                              (NEW – opens by ref-no)
  useEffect(() => {
    if (!library) return;
    const of = searchParams.get("openForm");
    const hl = searchParams.get("highlight") || "";
    const rf = searchParams.get("ref");
    let toOpen = null;
    if (of && isFarForm(of)) {     // Batch 33 · B33-4: 316(a) / R316(a) → the review screen (one place to work and sign)
      if (!engTypeReady) return;   // PR 51 · P51-B2: known once the engagement type is loaded
      if (opensFarScreen(of, engType)) { navigate(FAR_ROUTE, { replace: true }); return; }
    }
    if (of) {
      // Batch 13 · B1 (F-94): an id that is not a form of this engagement (unknown, or removed to the Recycle Bin)
      // → a plain toast; the page stays usable (before: a "Loading form…" dialog that never finished)
      const known = asArray(library.forms).some((f) => f.formId === of);
      const included = !includedIds || includedIds.has(of);
      if (known && included) toOpen = of;
      else {
        toast.error(t("formsPage.toastFormNotFound"));
        const next = new URLSearchParams(searchParams);
        next.delete("openForm"); next.delete("highlight"); next.delete("ref");
        setSearchParams(next, { replace: true });
      }
    }
    else if (rf) {
      const target = asArray(library.forms).find(
        (f) => (f.ref_no || "").toLowerCase() === rf.toLowerCase()
      );
      if (target) toOpen = target.formId;
      else toOpen = null;
      if (!target && rf) toast.error(t("formsPage.toastNoRefMatch").replace("{ref}", rf));
    }
    if (toOpen) {
      setOpenFormId(toOpen);
      setHighlight(hl);
      // Auto-expand the folder that contains this form (if any).
      const tabDef = library.structure?.tabs?.find((t) => t.tabId === tabId);
      const folder = tabDef?.folders?.find((fo) => (fo.forms || []).includes(toOpen));
      if (folder) setOpenFolders((s) => ({ ...s, [folder.folderId]: true }));
      // Clean URL params so refresh doesn't re-open.
      const next = new URLSearchParams(searchParams);
      next.delete("openForm"); next.delete("highlight"); next.delete("ref");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams.toString(), library, tabId, includedIds, engTypeReady, engType]);

  const loadLibrary = useEffectEvent(() => {   // once per mount, with the engagement open at that time (as before)
    api.get("/forms/library", { params: currentEngId ? { engagement_id: currentEngId } : undefined }).then((r) => setLibrary(r.data)).catch(() => {});
  });
  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  // Batch 33 · B33-4: a click on 316(a) / R316(a) in the list or the ref search opens the review screen, not the form dialog
  useEffect(() => {
    if (openFormId && isFarForm(openFormId) && engTypeReady && opensFarScreen(openFormId, engType)) { setOpenFormId(null); navigate(FAR_ROUTE); }
  }, [openFormId, navigate, engTypeReady, engType]);

  useEffect(() => {
    if (!currentEngId) return;
    api.get(`/forms/${currentEngId}/progress`)
      .then((r) => setProgress(progressOf(r.data))).catch(() => {});
    api.get(`/forms/preferences/${tabId}`)
      .then((r) => setPrefs(r.data || { folder_order: [], form_order: {} }))
      .catch(() => {});
    // Batch 0 corrections F — pull engagement type for review-body filter.
    api.get(`/engagements/${currentEngId}`, { _silent: true })
      .then((r) => setEngType(r.data?.engagement_type || "audit"))
      .catch(() => {})
      .finally(() => setEngTypeReady(true));
    // K5 — pull the AUDIT_ONLY_FORMS list for this engagement so the
    // Planning list can grey (not hide) audit-only rows on reviews.
    api.get(`/engagements/${currentEngId}/profile`, { _silent: true })
      .then((r) => setAuditOnlyForms(new Set(r.data?.audit_only_forms || [])))
      .catch(() => setAuditOnlyForms(new Set()));
  }, [currentEngId, tabId, openFormId]);

  const rawTab = useMemo(() => {
    if (!library) return null;
    return library.structure?.tabs?.find((t) => t.tabId === tabId);
  }, [library, tabId]);
  // Batch 0 corrections F+2 — Review Body Filter. If the engagement type
  // is 'review', hide non-review-default forms (they stay addable via the
  // library popup — hide-not-delete). K5 override: AUDIT_ONLY_FORMS are
  // NEVER hidden — they show greyed with a 🔒 chip + enable-with-reason.
  const tab = useMemo(() => {
    if (!rawTab) return null;
    if (engType !== "review") return rawTab;
    const nextFolders = asArray(rawTab.folders).map((fld) => ({
      ...fld,
      forms: asArray(fld.forms).filter((fid) => {
        const fdef = asArray(library?.forms).find((x) => x.formId === fid);
        return (fdef && fdef.reviewDefault)
          || (progress?.per_form?.[fid]?.in_engagement)
          // K5 — always show audit-only forms on reviews (greyed row).
          || auditOnlyForms.has(fid);
      }),
    })).filter((fld) => (fld.forms || []).length > 0);
    return { ...rawTab, folders: nextFolders };
  }, [rawTab, engType, library, progress, auditOnlyForms]);

  // Forms restricted to this tab, hydrated with definitions.
  // Round 3+ Document Library: also filter out forms that have been
  // deleted from this engagement or never added (add-on forms).
  const tabForms = useMemo(() => {
    if (!tab || !library) return [];
    const ids = new Set();
    asArray(tab.folders).forEach((fo) => asArray(fo.forms).forEach((fid) => ids.add(fid)));
    let all = asArray(library.forms).filter((f) => ids.has(f.formId));
    if (includedIds) all = all.filter((f) => includedIds.has(f.formId));
    return all;
  }, [tab, library, includedIds]);

  // Ref-no search matches — visible while the user is typing.
  const searchMatches = useMemo(() => {
    const q = refQuery.trim().toLowerCase();
    if (!q) return [];
    return tabForms
      .filter((f) => {
        const r = (f.ref_no || "").toLowerCase();
        const t = (f.title || "").toLowerCase();
        return r.includes(q) || t.includes(q);
      })
      .sort((a, b) => {
        // Prioritise ref starts-with, then contains, then title matches.
        const ar = (a.ref_no || "").toLowerCase();
        const br = (b.ref_no || "").toLowerCase();
        const aStart = ar.startsWith(q) ? 0 : ar.includes(q) ? 1 : 2;
        const bStart = br.startsWith(q) ? 0 : br.includes(q) ? 1 : 2;
        if (aStart !== bStart) return aStart - bStart;
        return cmpRef(a.ref_no, b.ref_no);
      })
      .slice(0, 8);
  }, [refQuery, tabForms]);

  // Batch 22 · Add-on 1 (items 1 + 4): a folder may carry LINKS to forms that live under another tab (the library's
  // "links": [{formId, to, note, note_ar}]). A link row opens the SAME form record on its own page — it is never a second
  // form: it is not in tabForms (search), not in the progress totals (the backend iterates folder.forms only), not in the
  // PDF, not in roll-forward. A link shows only when the engagement includes the form (a review engagement does not).
  // (a review engagement: the form is audit-only there — the link is hidden, like the card says; the K5 greyed row stays on
  // the Controls page itself)
  const visibleLinks = useCallback((folder) => asArray(folder?.links).filter((l) => l && l.formId && (!includedIds || includedIds.has(l.formId))
                                                                   && !(engType === "review" && auditOnlyForms.has(l.formId))),
                                     [includedIds, engType, auditOnlyForms]);

  const orderedFolders = useMemo(() => {
    if (!tab) return [];
    // item 4 — the empty-folder rule: a folder with no forms and no link for this engagement is HIDDEN (never "0 forms")
    const folders = [...(tab.folders || [])].filter((fo) => asArray(fo.forms).length > 0 || visibleLinks(fo).length > 0);
    const order = prefs.folder_order || [];
    folders.sort((a, b) => {
      const ai = order.indexOf(a.folderId);
      const bi = order.indexOf(b.folderId);
      // Batch 31 · item 18 (M83): a folder with no "order" goes after the ordered ones (it used to sort as 0, i.e. first —
      // the Planning tab's Specialized folder showed at the top)
      if (ai === -1 && bi === -1) return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return folders;
  }, [tab, prefs, visibleLinks]);

  // Flat list of forms in this tab, sorted numerically by ref_no.
  // Used when sortMode === "ref-no".
  const flatByRef = useMemo(() => {
    return [...tabForms].sort((a, b) => cmpRef(a.ref_no, b.ref_no));
  }, [tabForms]);

  const persistPrefs = async (next) => {
    setPrefs(next);
    if (currentEngId) {
      try { await api.patch(`/forms/preferences/${tabId}`, next); } catch {}
    }
  };

  // D19 (owner decision, 07-Oct-2026): "AI Suggest All" is hidden from the tenant portal (no Founder switch);
  // the per-form AI suggest stays. The route refuses until the next batch's fix.

  const moveFolder = async (idx, dir) => {
    const cur = orderedFolders.map((f) => f.folderId);
    const target = idx + dir;
    if (target < 0 || target >= cur.length) return;
    [cur[idx], cur[target]] = [cur[target], cur[idx]];
    await persistPrefs({ ...prefs, folder_order: cur });
  };

  const reorderFolderTo = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const cur = orderedFolders.map((f) => f.folderId);
    const [it] = cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, it);
    await persistPrefs({ ...prefs, folder_order: cur });
  };
  const reorderFormTo = async (folderId, forms, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const cur = [...forms];
    const [it] = cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, it);
    await persistPrefs({ ...prefs, form_order: { ...prefs.form_order, [folderId]: cur } });
  };

  const exportPdf = async () => {
    try {
      const resp = await api.get(`/forms/${currentEngId}/export/pdf?tab=${tabId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pageTitle.replace(/[\s()]+/g,"_").replace(/_+$/,"")}_${currentEngId.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("formsPage.pdfReady"));
    } catch (e) { toast.error(t("formsPage.pdfFailed")); }
  };

  const rollForward = async () => {
    // Opens a dialog first — Round 3+: auditor decides whether to also
    // carry over any add-on forms that were added in the prior year
    // (checkbox defaults to Yes so audit programme stays stable
    // year-over-year, matching option 1c).
    setCarryAddons(true);
    setRollFwdOpen(true);
  };

  const commitRollForward = async () => {
    setRollBusy(true);
    try {
      const { data } = await api.post(
        `/forms/${currentEngId}/roll-forward`,
        { carry_over_addons: carryAddons },
      );
      const msg = data.copied === 0
        ? t("formsPage.noRollMsg")
        : t("formsPage.rolledMsg")
            .replace("{n}", fmtPlural(data.copied, lang, dict.plurals.forms))
            .replace("{year}", data.from_year);
      const addonMsg = data.addons_carried > 0
        ? t("formsPage.addonsCarriedMsg").replace("{n}", fmtPlural(data.addons_carried, lang, dict.plurals.addonForms))
        : "";
      if (data.copied === 0 && data.addons_carried === 0) toast.info(msg);
      else toast.success(msg + addonMsg);
      const r = await api.get(`/forms/${currentEngId}/progress`);
      setProgress(progressOf(r.data));
      setRollFwdOpen(false);
    } catch (e) {
      toast.error(toErrMsg(e, t("formsPage.rollFailed")));
    } finally { setRollBusy(false); }
  };

  const moveForm = async (folderId, forms, idx, dir) => {
    const cur = [...forms];
    const target = idx + dir;
    if (target < 0 || target >= cur.length) return;
    [cur[idx], cur[target]] = [cur[target], cur[idx]];
    await persistPrefs({ ...prefs, form_order: { ...prefs.form_order, [folderId]: cur } });
  };

  const orderedForms = (folder) => {
    const base = folder.forms || [];
    const override = prefs.form_order?.[folder.folderId];
    if (!override) return base;
    return [...override.filter((f) => base.includes(f)), ...base.filter((f) => !override.includes(f))];
  };

  const jumpToForm = (formId) => {
    if (!formId) return;
    // Expand the containing folder for continuity when user closes the modal.
    const folder = asArray(tab?.folders).find((fo) => (fo.forms || []).includes(formId));
    if (folder) setOpenFolders((s) => ({ ...s, [folder.folderId]: true }));
    setOpenFormId(formId);
    setRefQuery("");
    setRefFocus(false);
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchMatches.length > 0) jumpToForm(searchMatches[0].formId);
      else toast.info(t("formsPage.noQueryMatch").replace("{q}", refQuery));
    } else if (e.key === "Escape") {
      setRefQuery(""); setRefFocus(false); refInputRef.current?.blur();
    }
  };

  if (!library) return <div className="p-6 text-sm text-muted-foreground">{t("formsPage.loading")}</div>;
  if (!currentEngId) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          {t("formsPage.selectEngagement").replace("{tab}", pageTitle)}
        </CardContent></Card>
      </div>
    );
  }
  if (!tab) return <div className="p-6 text-sm text-red-600">{t("formsPage.tabNotFound").replace("{tabId}", tabId)}</div>;

  const tabPct = progress.per_tab?.[tabId]?.pct ?? 0;

  // Batch 22 · Add-on 1 (item 1): the link row — same ref, same title, the same completion count as the form itself
  const renderLinkRow = (link, folder) => {
    const fdef = library.forms?.find((f) => f.formId === link.formId);
    const p = progress.per_form?.[link.formId];
    const note = (lang === "ar" && link.note_ar) ? link.note_ar : (link.note || "");
    return (
      <div key={`link-${link.formId}`} className="flex items-center justify-between rounded border border-dashed p-2 hover:bg-slate-50"
           data-testid={`form-link-${link.formId}`}>
        {fdef?.ref_no && (
          <Badge variant="outline" className="ms-1 font-mono text-[10px] text-slate-600 flex-none"><Ref>{fdef.ref_no}</Ref></Badge>
        )}
        <button className="flex flex-1 items-center gap-2 text-start ms-2"
                onClick={() => navigate(`${link.to}?openForm=${encodeURIComponent(link.formId)}`)}
                data-testid={`form-link-open-${link.formId}`}>
          <ExternalLink className="h-4 w-4 text-emerald-700" />
          <span className="text-sm"><Ref>{(lang === "ar" && fdef?.title_ar) ? fdef.title_ar : (fdef?.title || link.formId)}</Ref></span>
          {p && !p.na && <Badge variant="outline" className="text-[10px]">{p.done}/{p.total}</Badge>}
          {p?.na && <Badge variant="outline" className="text-[10px]">{t("formsPage.notApplicableBadge")}</Badge>}
          <span className="text-[11px] text-muted-foreground" data-testid={`form-link-note-${link.formId}`}>{note}</span>
        </button>
      </div>
    );
  };

  const renderFormRow = (formId, formIdx, folder, forms, opts = {}) => {
    const fdef = library.forms?.find((f) => f.formId === formId);
    const p = progress.per_form?.[formId];
    const isNA = p?.na;
    // K5 — greyed = audit-only form on a Review engagement (A1 rule).
    // Row remains clickable; the modal itself already carries the
    // enable-with-reason guard.
    const isGreyed = engType === "review" && auditOnlyForms.has(formId);
    const draggable = !opts.flat;
    const rpTargetType = TAB_RP_TARGET[tabId];
    return (
      <div key={formId}
           draggable={draggable}
           onDragStart={(e) => { if (!draggable) return; e.stopPropagation(); _DND.source = { kind: "form", folderId: folder.folderId, from: formIdx }; e.dataTransfer.effectAllowed = "move"; }}
           onDragOver={(e) => { if (!draggable) return; if (_DND.source?.kind === "form" && _DND.source.folderId === folder.folderId) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
           onDrop={(e) => { if (!draggable) return; if (_DND.source?.kind === "form" && _DND.source.folderId === folder.folderId) { e.preventDefault(); e.stopPropagation(); reorderFormTo(folder.folderId, forms, _DND.source.from, formIdx); _DND.source = null; } }}
           className={`flex items-center justify-between rounded border p-2 hover:bg-slate-50 ${isNA || isGreyed ? "opacity-50" : ""}`}
           title={isGreyed ? t("review.notUsed") : undefined}
           data-testid={`form-row-${formId}`}>
        {draggable && <GripVertical className="h-3 w-3 text-slate-300 cursor-grab flex-none" />}
        {fdef?.ref_no && (
          <Badge variant="outline" className="ms-1 font-mono text-[10px] text-slate-600 flex-none">
            <Ref>{fdef.ref_no}</Ref>
          </Badge>
        )}
        <button className="flex flex-1 items-center gap-2 text-start ms-2"
                onClick={() => setOpenFormId(formId)}
                data-testid={`form-open-${formId}`}>
          <FileText className="h-4 w-4 text-slate-400" />
          <span className="text-sm"><Ref>{(lang === "ar" && fdef?.title_ar) ? fdef.title_ar : (fdef?.title || formId)}</Ref></span>
          {isNA && <Badge variant="outline" className="text-[10px]">{t("formsPage.notApplicableBadge")}</Badge>}
          {isGreyed && (
            <Badge variant="outline" className="text-[10px]"
                   data-testid={`form-row-${formId}-audit-only`}>
              🔒 {t("review.notUsed")}
            </Badge>
          )}
          {!isNA && p && (
            <Badge variant="outline" className="text-[10px]">
              {p.done}/{p.total}
            </Badge>
          )}
          {p?.carried && (   /* Batch 10.1 · R2 — carried from last year: not complete until saved again */
            <Badge variant="outline" className="border-sky-300 bg-sky-50 text-[10px] text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                   data-testid={`form-row-${formId}-carried`}>
              {t("common.carriedForward")}
            </Badge>
          )}
        </button>
        <div className="flex items-center gap-1">
          {rpTargetType && (
            <div onClick={(e) => e.stopPropagation()}
                  data-testid={`form-rp-${formId}`}>
              <ReviewControls
                engagementId={currentEngId}
                targetType={rpTargetType}
                targetId={formId}
                targetLabel={`${fdef?.ref_no || ""} ${fdef?.title || formId}`}
                compact />
            </div>
          )}
          {!opts.flat && (
            <>
              <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => moveForm(folder.folderId, forms, formIdx, -1)}
                      disabled={formIdx === 0}
                      data-testid={`form-up-${formId}`}>
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => moveForm(folder.folderId, forms, formIdx, 1)}
                      disabled={formIdx === forms.length - 1}
                      data-testid={`form-down-${formId}`}>
                <ArrowDown className="h-3 w-3" />
              </Button>
            </>
          )}
          <Button size="icon" variant="ghost" className="h-6 w-6" title={isNA ? t("formsPage.markApplicableTitle") : t("formsPage.markNaTitle")}
                  onClick={async () => {
                    // Batch 14 · M11 (F-110): marking N/A needs a short reason (ISA 230 — why); shown on the form and in the sign-offs view
                    let reason = "";
                    if (!isNA) {
                      reason = (await askPrompt(t("formsPage.naReasonPrompt")) || "").trim();
                      if (reason.length < 3) { toast.error(t("formsPage.naReasonRequired")); return; }
                    }
                    try { await api.post(`/forms/${currentEngId}/${formId}/na`, { is_not_applicable: !isNA, reason }); }
                    catch (e) { toast.error(toErrMsg(e, t("common.failed"))); return; }
                    toast.success(isNA ? t("formsPage.markedApplicable") : t("formsPage.markedNa"));
                    const r = await api.get(`/forms/${currentEngId}/progress`);
                    setProgress(progressOf(r.data));
                  }}
                  data-testid={`form-na-${formId}`}>
            <EyeOff className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" title={t("formsPage.deletePermanentlyTitle")}
                  onClick={() => {
                    setDeleteTarget({ form_id: formId,
                                       ref_no: fdef?.ref_no || "",
                                       title: fdef?.title || formId });
                  }}
                  data-testid={`form-delete-${formId}`}>
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
    <ReviewCountsProvider engagementId={currentEngId} targetType={TAB_RP_TARGET[tabId]}>
    <div className="space-y-4" data-testid={`forms-page-${tabId}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold" data-testid={`forms-title-${tabId}`}>{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {t("formsPage.totalItems")
              .replace("{folders}", (tab.folders || []).length)
              .replace("{total}", progress.per_tab?.[tabId]?.total || 0)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Ref-No search */}
          <div className="relative">
            <div className="relative">
              <Search className="pointer-events-none absolute start-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                ref={refInputRef}
                value={refQuery}
                onChange={(e) => setRefQuery(e.target.value)}
                onFocus={() => setRefFocus(true)}
                onBlur={() => setTimeout(() => setRefFocus(false), 150)}
                onKeyDown={handleSearchKey}
                placeholder={t("formsPage.searchPlaceholder")}
                className="h-8 w-56 ps-7 pe-6 text-xs"
                data-testid={`forms-search-${tabId}`}
              />
              {refQuery && (
                <button
                  type="button"
                  onClick={() => { setRefQuery(""); refInputRef.current?.focus(); }}
                  className="absolute end-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid={`forms-search-clear-${tabId}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {refFocus && refQuery && (
              <div className="absolute start-0 end-0 top-full z-40 mt-1 max-h-72 overflow-auto rounded border bg-white shadow-lg dark:bg-slate-900"
                   data-testid={`forms-search-results-${tabId}`}>
                {searchMatches.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">{t("formsPage.noMatches")}</div>
                )}
                {searchMatches.map((f) => (
                  <button key={f.formId}
                          onMouseDown={(e) => { e.preventDefault(); jumpToForm(f.formId); }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-start text-xs hover:bg-slate-50"
                          data-testid={`forms-search-result-${f.formId}`}>
                    {/* K2 — LTR-isolate the ref explicitly with U+2068/U+2069
                        so the parens don't visually reverse inside an
                        Arabic (RTL) dropdown container. `<Ref>` already
                        uses `<bdi>`, but a stray `direction:rtl` from
                        the parent flexbox can still flip the parens. */}
                    <Badge variant="outline" className="font-mono text-[10px]" dir="ltr" data-testid={`forms-search-result-ref-${f.formId}`}>
                      <Ref>{`\u2068${f.ref_no || "—"}\u2069`}</Ref>
                    </Badge>
                    <span className="break-words">{(lang === "ar" && f.title_ar) ? f.title_ar : f.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort toggle */}
          <div className="inline-flex overflow-hidden rounded border text-xs"
               data-testid={`forms-sort-toggle-${tabId}`}>
            <button
              type="button"
              className={`px-2 py-1 ${sortMode === "folder" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setSortMode("folder")}
              data-testid={`forms-sort-folder-${tabId}`}>
              {t("formsPage.sortFolder")}
            </button>
            <button
              type="button"
              className={`px-2 py-1 ${sortMode === "ref-no" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              onClick={() => setSortMode("ref-no")}
              data-testid={`forms-sort-refno-${tabId}`}>
              {t("formsPage.sortRefNo")}
            </button>
          </div>

          <Button size="sm" variant="outline" onClick={rollForward} data-testid={`roll-forward-${tabId}`}>
            <History className="me-1 h-3 w-3" /> {t("formsPage.rollForwardBtn")}
          </Button>
          <Button size="sm" variant="outline" onClick={exportPdf} data-testid={`export-pdf-${tabId}`}>
            <Download className="me-1 h-3 w-3" /> {t("formsPage.exportPdfBtn")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setLibOpen(true)}
                  data-testid={`add-from-library-${tabId}`}>
            <Library className="me-1 h-3 w-3" /> {t("headFolders.addFromLibrary")}
          </Button>
          <div className="text-end">
            <div className="text-xs text-muted-foreground">{t("formsPage.completionLabel")}</div>
            <div className="text-lg font-semibold" data-testid={`tab-completion-${tabId}`}>{tabPct}%</div>
          </div>
          <div className="h-16 w-16 rounded-full border-4"
               style={{ borderColor: tabPct >= 80 ? "#059669" : tabPct >= 40 ? "#f59e0b" : "#e2e8f0" }} />
        </div>
      </div>

      {sortMode === "ref-no" ? (
        // Flat list ordered numerically by ref_no. Drag-reorder is disabled in this mode
        // since the user is explicitly asking for a canonical numeric order.
        <Card data-testid={`forms-flat-refno-${tabId}`}>
          <CardHeader className="py-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-slate-500" />
              <span>{t("formsPage.allByRef")}</span>
              <Badge variant="outline" className="ms-2 text-[10px]">{fmtPlural(flatByRef.length, lang, dict.plurals.forms)}</Badge>
              <span className="ms-2 text-[11px] font-normal text-muted-foreground">
                {t("formsPage.dragDisabledPre")}<b>{t("formsPage.dragDisabledFolderBold")}</b>{t("formsPage.dragDisabledPost")}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1">
              {flatByRef.map((f, idx) => {
                // synthesise a pseudo-folder just for the shared row renderer.
                const pseudoFolder = { folderId: "__flat__" };
                return renderFormRow(f.formId, idx, pseudoFolder, flatByRef.map((x) => x.formId), { flat: true });
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {orderedFolders.map((folder, fIdx) => {
            const isOpen = openFolders[folder.folderId] ?? true;
            const forms = orderedForms(folder);
            const links = visibleLinks(folder);
            return (
              <Card key={folder.folderId} data-testid={`folder-${folder.folderId}`}
                    draggable
                    onDragStart={(e) => { _DND.source = { kind: "folder", from: fIdx }; e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { if (_DND.source?.kind === "folder") { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                    onDrop={(e) => { if (_DND.source?.kind === "folder") { e.preventDefault(); reorderFolderTo(_DND.source.from, fIdx); _DND.source = null; } }}>
                <CardHeader className="cursor-pointer py-3"
                            onClick={() => setOpenFolders({ ...openFolders, [folder.folderId]: !isOpen })}>
                  <CardTitle className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-slate-300 cursor-grab" />
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <FolderOpen className="h-4 w-4 text-slate-500" />
                      <span>{(lang === "ar" && (dict.formsPage?.folders?.[folder.folderId])) ? dict.formsPage.folders[folder.folderId] : folder.folderName}</span>
                      <Badge variant="outline" className="ms-2 text-[10px]">
                        {forms.length > 0 || links.length === 0 ? fmtPlural(forms.length, lang, dict.plurals.forms) : fmtPlural(links.length, lang, dict.plurals.links)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveFolder(fIdx, -1)}
                              disabled={fIdx === 0} data-testid={`folder-up-${folder.folderId}`}>
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveFolder(fIdx, 1)}
                              disabled={fIdx === orderedFolders.length - 1} data-testid={`folder-down-${folder.folderId}`}>
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                {isOpen && (
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {forms.map((formId, formIdx) => renderFormRow(formId, formIdx, folder, forms))}
                      {links.map((l) => renderLinkRow(l, folder))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {openFormId && (!isFarForm(openFormId) || (engTypeReady && !opensFarScreen(openFormId, engType))) && (
        <FormRenderer
          engagementId={currentEngId}
          formId={openFormId}
          readOnly={isLegacy316a(openFormId, engType)}
          highlight={highlight}
          onClose={() => { setOpenFormId(null); setHighlight(""); }}
        />
      )}

      <FormPermanentDeleteDialog
        open={!!deleteTarget}
        form={deleteTarget}
        engagementId={currentEngId}
        onClose={() => setDeleteTarget(null)}
        onDeleted={async () => {
          setDeleteTarget(null);
          setOpenFormId(null);
          try {
            const r = await api.get(`/forms/${currentEngId}/progress`);
            setProgress(progressOf(r.data));
          } catch {}
        }}
      />

      {/* Roll-forward dialog — carry-over choice for add-on forms */}
      <Dialog open={rollFwdOpen} onOpenChange={(o) => !o && setRollFwdOpen(false)}>
        <DialogContent data-testid="roll-forward-dialog">
          <DialogHeader>
            <DialogTitle>{t("formsPage.rollDialogTitle")}</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <div>
                {t("formsPage.rollDialogBodyPre")}<strong>{t("formsPage.rollDialogBodyBold")}</strong>{t("formsPage.rollDialogBodyPost")}
              </div>
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 rounded-md border bg-slate-50 p-3 text-sm cursor-pointer"
                 data-testid="roll-forward-carry-addons-row">
            <input type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={carryAddons}
                    onChange={(e) => setCarryAddons(e.target.checked)}
                    data-testid="roll-forward-carry-addons" />
            <span>
              <strong>{t("formsPage.carryAddonsBold")}</strong>{t("formsPage.carryAddonsBody")}
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollFwdOpen(false)}
                    disabled={rollBusy}
                    data-testid="roll-forward-cancel">
              {t("formsPage.cancelBtn")}
            </Button>
            <Button onClick={commitRollForward} disabled={rollBusy}
                    data-testid="roll-forward-confirm">
              {rollBusy ? t("formsPage.rollingLabel") : t("formsPage.continueBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ReviewCountsProvider>
    <AddFromLibraryDialog open={libOpen} onOpenChange={setLibOpen} filterTabId={tabId}
                            onAdded={() => { api.get(`/forms/${currentEngId}/progress`).then((r) => setProgress(progressOf(r.data))).catch(() => {}); }} />
    </>
  );
}
